import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  TextField,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AddSalaryComponentDialog from "components/AddSalaryComponentDialog";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import LoadingScreen from "components/Loading";
import { useMaterialUIController } from "context";
import { useAppTheme } from "context/themeContext";
import {
  getSalaryComponents,
  addSalaryComponent,
  updateSalaryComponent,
  deleteSalaryComponent,
} from "services/api";

const ComponentsView = ({ canEdit }) => {
  const { t } = useTranslation();
  const { primaryColor } = useAppTheme();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const snackbarRef = useRef();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [componentToDelete, setComponentToDelete] = useState(null);

  useEffect(() => {
    const fetchComponents = async () => {
      setLoading(true);
      const res = await getSalaryComponents();
      if (res !== "error") {
        setComponents(res);
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.components.loadError"), "error");
      }
      setLoading(false);
    };
    fetchComponents();
  }, []);

  const filteredComponents = components.filter(
    (c) =>
      c.code?.toLowerCase().includes(search.toLowerCase()) ||
      c.name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenAdd = () => {
    setSelectedComponent(null);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleOpenEdit = (component) => {
    setSelectedComponent(component);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSubmit = async (form) => {
    if (editMode) {
      const res = await updateSalaryComponent(selectedComponent.id, form);
      if (res !== "error") {
        setComponents(components.map((c) => (c.id === res.id ? res : c)));
        snackbarRef.current?.showSnackbar(t("admin.payroll.components.updateSuccess"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.components.updateError"), "error");
      }
    } else {
      const res = await addSalaryComponent(form);
      if (res !== "error") {
        setComponents([...components, res]);
        snackbarRef.current?.showSnackbar(t("admin.payroll.components.addSuccess"), "success");
      } else {
        snackbarRef.current?.showSnackbar(t("admin.payroll.components.addError"), "error");
      }
    }
    setDialogOpen(false);
  };

  const handleDeleteClick = () => {
    setDialogOpen(false);
    setComponentToDelete(selectedComponent);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    const res = await deleteSalaryComponent(componentToDelete.id);
    if (res === "success") {
      setComponents(components.filter((c) => c.id !== componentToDelete.id));
      snackbarRef.current?.showSnackbar(t("admin.payroll.components.deleteSuccess"), "success");
    } else if (typeof res === "string" && res.startsWith("Cannot delete")) {
      // Backend guard: component is referenced by a structure/input/result — deactivate instead
      snackbarRef.current?.showSnackbar(t("admin.payroll.components.cannotDelete"), "error");
    } else {
      snackbarRef.current?.showSnackbar(t("admin.payroll.components.deleteError"), "error");
    }
    setConfirmOpen(false);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <MDTypography variant="subtitle1" fontWeight="bold">
          {t("admin.payroll.components.count")} {components.length}
        </MDTypography>
        <MDButton color="primary" onClick={handleOpenAdd} disabled={!canEdit}>
          {t("admin.payroll.components.addTitle")}
        </MDButton>
      </MDBox>

      <TextField
        label={t("admin.payroll.components.search")}
        variant="outlined"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
      />

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: darkMode ? "#333" : "#fff",
          border: darkMode ? "2px solid " + primaryColor : null,
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          borderRadius: 2,
          mb: 4,
        }}
      >
        <Table>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.components.fields.code")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.components.fields.name")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("admin.payroll.components.fields.type")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="center">
                {t("admin.payroll.components.fields.active")}
              </TableCell>
            </TableRow>
            {filteredComponents.map((component) => (
              <TableRow
                key={component.id}
                hover
                sx={{
                  cursor: canEdit ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
                onClick={() => canEdit && handleOpenEdit(component)}
              >
                <TableCell>{component.code}</TableCell>
                <TableCell>{component.name}</TableCell>
                <TableCell>
                  <Chip
                    label={t(`admin.payroll.components.types.${toCamel(component.type)}`)}
                    size="small"
                    color={
                      component.type === "earning"
                        ? "success"
                        : component.type === "deduction"
                          ? "error"
                          : "info"
                    }
                  />
                </TableCell>
                <TableCell align="center">{component.active ? "✓" : "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <AddSalaryComponentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        salaryComponent={selectedComponent}
        editMode={editMode}
        handleDelete={handleDeleteClick}
      />

      <ConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        message={t("admin.payroll.components.confirmDelete", { name: componentToDelete?.name })}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
};

// employer_contribution -> employerContribution, for the i18n key lookup above
function toCamel(str) {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

ComponentsView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default ComponentsView;
